import { Link } from 'react-router-dom';

interface ButtonProps {
  children: React.ReactNode;
  to?: string;
  href?: string;
  variant?: 'primary' | 'accent' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
  type?: 'button' | 'submit';
  icon?: string;
  iconRight?: string;
  external?: boolean;
  /** GA4/GTMクリック計測用: data-cta-name（CTAの種類） */
  dataCtaName?: string;
  /** GA4/GTMクリック計測用: data-cta-location（設置場所） */
  dataCtaLocation?: string;
}

export default function Button({
  children,
  to,
  href,
  variant = 'primary',
  size = 'md',
  className = '',
  onClick,
  type = 'button',
  icon,
  iconRight,
  external = false,
  dataCtaName,
  dataCtaLocation,
}: ButtonProps) {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-sm',
    lg: 'px-8 py-4 text-base',
  };

  const variantClasses = {
    primary: 'bg-primary-500 text-background-50 hover:bg-primary-600 focus:ring-primary-400',
    accent: 'bg-accent-500 text-background-50 hover:bg-accent-600 focus:ring-accent-400',
    secondary: 'bg-secondary-500 text-background-50 hover:bg-secondary-600 focus:ring-secondary-400',
    outline: 'border-2 border-primary-500 text-primary-600 hover:bg-primary-50 focus:ring-primary-400',
    ghost: 'text-foreground-700 hover:bg-background-200 focus:ring-foreground-400',
  };

  const baseClasses = `whitespace-nowrap inline-flex items-center justify-center gap-2 rounded-md font-medium transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-1 ${sizeClasses[size]} ${variantClasses[variant]} ${className}`;

  // GA4/GTMクリック計測用のdata属性（値がある時だけ付与。既存ボタンには影響なし）
  const ctaAttrs: Record<string, string> = {};
  if (dataCtaName) ctaAttrs['data-cta-name'] = dataCtaName;
  if (dataCtaLocation) ctaAttrs['data-cta-location'] = dataCtaLocation;

  if (to) {
    return (
      <Link to={to} className={baseClasses} {...ctaAttrs}>
        {icon && <i className={icon}></i>}
        {children}
        {iconRight && <i className={iconRight}></i>}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} className={baseClasses} {...ctaAttrs} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
        {icon && <i className={icon}></i>}
        {children}
        {iconRight && <i className={iconRight}></i>}
      </a>
    );
  }

  return (
    <button type={type} className={baseClasses} onClick={onClick} {...ctaAttrs}>
      {icon && <i className={icon}></i>}
      {children}
      {iconRight && <i className={iconRight}></i>}
    </button>
  );
}
